// src/components/demo/DemoScanner.js
//
// In-app QR camera scanner (SPEC §7.3). Board QRs encode
// https://my.waka.surf/r/{SKU}; skuFromScan() extracts + validates the SKU.
//
// Uses @zxing/browser (BrowserQRCodeReader) against a <video> element with the
// rear camera. Manual SKU entry is always available as the fallback — camera
// permission denial or unsupported browsers never block a check-out.

import React, { useEffect, useRef, useState } from 'react';
import { BrowserQRCodeReader } from '@zxing/browser';
import { skuFromScan } from '../../services/demoService';
import { FLabel } from '../foam/FoamPrimitives';

export default function DemoScanner({ onSku, onError }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const doneRef = useRef(false);
  const [status, setStatus] = useState('starting'); // starting | scanning | denied | unsupported

  useEffect(() => {
    let cancelled = false;
    const reader = new BrowserQRCodeReader();

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) { setStatus('unsupported'); return; }
      try {
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: 'environment' } },
          videoRef.current,
          (result) => {
            if (!result || doneRef.current || cancelled) return;
            const sku = skuFromScan(result.getText());
            if (sku) {
              doneRef.current = true;
              try { navigator.vibrate && navigator.vibrate(60); } catch { /* no-op */ }
              controlsRef.current?.stop();
              onSku(sku);
            }
            // Non-WAKA QR payloads are ignored; keep scanning.
          },
        );
        if (cancelled) { controls.stop(); return; }
        controlsRef.current = controls;
        setStatus('scanning');
      } catch (e) {
        setStatus(e?.name === 'NotAllowedError' ? 'denied' : 'unsupported');
        onError && onError(e);
      }
    }
    start();

    return () => {
      cancelled = true;
      controlsRef.current?.stop();
    };
  }, [onSku, onError]);

  return (
    <div className="relative w-full" style={{ background: 'var(--foam-carbon)', aspectRatio: '3 / 4' }}>
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        muted
        playsInline
        style={{ display: status === 'scanning' ? 'block' : 'none' }}
      />
      {/* Viewfinder frame — orange L-corner brackets (design: DScanActive) */}
      {status === 'scanning' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div style={{ position: 'relative', width: '62%', aspectRatio: '1 / 1' }}>
            {[[0, 0], [1, 0], [0, 1], [1, 1]].map(([x, y], i) => (
              <div
                key={i}
                style={{
                  position: 'absolute', width: 40, height: 40,
                  [x ? 'right' : 'left']: 0, [y ? 'bottom' : 'top']: 0,
                  borderTop: !y ? '3px solid var(--foam-orange)' : 'none',
                  borderBottom: y ? '3px solid var(--foam-orange)' : 'none',
                  borderLeft: !x ? '3px solid var(--foam-orange)' : 'none',
                  borderRight: x ? '3px solid var(--foam-orange)' : 'none',
                }}
              />
            ))}
          </div>
        </div>
      )}
      {status !== 'scanning' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-[10px] px-[24px] text-center">
          <FLabel color="var(--foam-paper)">
            {status === 'starting' && 'Starting camera…'}
            {status === 'denied' && 'Camera access denied — allow camera in browser settings, or type the serial below'}
            {status === 'unsupported' && 'Camera unavailable — type the serial below'}
          </FLabel>
        </div>
      )}
    </div>
  );
}
